import React, { useEffect, useState } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { dashboardAPI, reportsAPI } from "../services/api";
import ReactApexChart from 'react-apexcharts';
import { Icon } from '@iconify/react/dist/iconify.js';

const DashboardDistrictPage = () => {
  const [stats, setStats] = useState(null);
  const [monthlyTrends, setMonthlyTrends] = useState(null);
  const [blockWiseTrends, setBlockWiseTrends] = useState(null);
  const [highRiskAnalysis, setHighRiskAnalysis] = useState(null);
  const [usgPerformance, setUSGPerformance] = useState(null);
  const [districtGrievanceTrends, setDistrictGrievanceTrends] = useState(null);
  const [districtHighRiskTimeline, setDistrictHighRiskTimeline] = useState(null);
  const [dpPerformance, setDpPerformance] = useState(null);
  const [deliverySummary, setDeliverySummary] = useState(null);
  const [ecgSummary, setEcgSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendStats, setTrendStats] = useState(null);
  const [riskViewFilter, setRiskViewFilter] = useState('district');
  const [ancViewFilter, setAncViewFilter] = useState('Yearly');
  const [selectedBlock, setSelectedBlock] = useState('all');
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, trendsData, blockData, riskData, usgData,
               grievanceTrendsData, highRiskTimelineData, dpPerfData, deliverySummaryData, ecgData
        ] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getMonthlyTrends(currentYear),
          dashboardAPI.getBlockWiseTrends(currentYear),
          dashboardAPI.getHighRiskAnalysis(),
          dashboardAPI.getUSGPerformance(),
          dashboardAPI.getDistrictGrievanceTrends(),
          dashboardAPI.getDistrictHighRiskTimeline(6),
          reportsAPI.getDeliveryPointPerformance(),
          reportsAPI.getDeliverySummary(),
          reportsAPI.getECGSummary()
        ]);

        setStats(statsData);
        setMonthlyTrends(trendsData);

        // Compute real trend % for stat cards
        // Compare current month vs previous month using monthly_data
        const monthly = trendsData?.monthly_data;
        if (monthly && monthly.length >= 2) {
          const now = new Date();
          const curIdx = now.getMonth(); // 0-based, matches array index (month 1 = index 0)
          const prevIdx = curIdx > 0 ? curIdx - 1 : null;

          const cur = monthly[curIdx]  || { registrations: 0, usg_appointments: 0, grievances: 0 };
          const prev = prevIdx !== null ? monthly[prevIdx] : null;

          const calcPct = (curVal, prevVal) => {
            if (!prev || prevVal === 0) return null;
            return Math.round(((curVal - prevVal) / prevVal) * 100);
          };

          setTrendStats({
            registrations: calcPct(cur.registrations, prev?.registrations),
            usg:           calcPct(cur.usg_appointments, prev?.usg_appointments),
            grievances:    calcPct(cur.grievances, prev?.grievances),
          });
        }

        setBlockWiseTrends(blockData);
        setHighRiskAnalysis(riskData);
        setUSGPerformance(usgData);
        setDistrictGrievanceTrends(grievanceTrendsData);
        setDistrictHighRiskTimeline(highRiskTimelineData);
        setDpPerformance(dpPerfData);
        setDeliverySummary(deliverySummaryData);
        setEcgSummary(ecgData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentYear]);

  useEffect(() => {
    const fetchANCTrends = async () => {
      try {
        let trendsData;
        if (ancViewFilter === 'Yearly') {
          trendsData = await dashboardAPI.getMonthlyTrends(currentYear);
        } else if (ancViewFilter === 'Monthly') {
          const currentMonth = new Date().getMonth() + 1;
          trendsData = await dashboardAPI.getDailyTrends(currentMonth, currentYear);
        } else if (ancViewFilter === 'Weekly') {
          trendsData = await dashboardAPI.getWeeklyTrends(currentYear);
        } else if (ancViewFilter === 'Today') {
          trendsData = await dashboardAPI.getTodayStats();
        }
        setMonthlyTrends(trendsData);
      } catch (error) {
        console.error('Error fetching ANC trends:', error);
      }
    };
    fetchANCTrends();
  }, [ancViewFilter, currentYear]);

  // Helper to render trend badge
  const TrendBadge = ({ value, label = 'vs last month' }) => {
    if (value === null || value === undefined) {
      return <span className="fw-medium text-sm text-secondary-light">{label}</span>;
    }
    const isUp = value >= 0;
    return (
      <p className="fw-medium text-sm text-primary-light mt-12 mb-0 d-flex align-items-center gap-2">
        <span className={`d-inline-flex align-items-center gap-1 ${ isUp ? 'text-success-main' : 'text-danger-main'}`}>
          <Icon icon={isUp ? 'bxs:up-arrow' : 'bxs:down-arrow'} className="text-xs" />
          {isUp ? '+' : ''}{value}%
        </span>
        {label}
      </p>
    );
  };

  // Chart configurations with real-time data only
  const getANCChartCategories = () => {
    if (ancViewFilter === 'Yearly') {
      return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    } else if (ancViewFilter === 'Monthly') {
      return monthlyTrends?.daily_data?.map(day => `Day ${day.day}`) || [];
    } else if (ancViewFilter === 'Weekly') {
      return monthlyTrends?.weekly_data?.map(week => `Week ${week.week}`) || [];
    } else if (ancViewFilter === 'Today') {
      return ['Today'];
    }
    return [];
  };

  const getANCChartData = () => {
    if (ancViewFilter === 'Yearly') {
      return monthlyTrends?.monthly_data?.map(month => month.registrations) || [];
    } else if (ancViewFilter === 'Monthly') {
      return monthlyTrends?.daily_data?.map(day => day.registrations) || [];
    } else if (ancViewFilter === 'Weekly') {
      return monthlyTrends?.weekly_data?.map(week => week.registrations) || [];
    } else if (ancViewFilter === 'Today') {
      return [monthlyTrends?.registrations_today || 0];
    }
    return [];
  };

  const getTotalVisits = () => {
    if (ancViewFilter === 'Today') {
      return monthlyTrends?.registrations_today || 0;
    } else if (ancViewFilter === 'Yearly') {
      return monthlyTrends?.monthly_data?.reduce((sum, m) => sum + m.registrations, 0) || 0;
    } else if (ancViewFilter === 'Monthly') {
      return monthlyTrends?.daily_data?.reduce((sum, d) => sum + d.registrations, 0) || 0;
    } else if (ancViewFilter === 'Weekly') {
      return monthlyTrends?.weekly_data?.reduce((sum, w) => sum + w.registrations, 0) || 0;
    }
    return 0;
  };

  const getTrendPercentage = () => {
    if (ancViewFilter === 'Today') return null;
    const data = ancViewFilter === 'Yearly'
      ? monthlyTrends?.monthly_data?.map(m => m.registrations)
      : ancViewFilter === 'Monthly'
      ? monthlyTrends?.daily_data?.map(d => d.registrations)
      : monthlyTrends?.weekly_data?.map(w => w.registrations);
    if (!data || data.length < 2) return null;
    const half = Math.floor(data.length / 2);
    const firstHalf = data.slice(0, half).reduce((s, v) => s + v, 0);
    const secondHalf = data.slice(half).reduce((s, v) => s + v, 0);
    if (firstHalf === 0) return null;
    return Math.round(((secondHalf - firstHalf) / firstHalf) * 100);
  };

  const monthlyTrendsChart = {
    series: [{
      name: 'Registrations',
      data: getANCChartData()
    }],
    options: {
      chart: { type: 'line', height: 300, toolbar: { show: false } },
      stroke: { curve: 'smooth', width: 3, colors: ['#487FFF'] },
      xaxis: { categories: getANCChartCategories() },
      grid: { borderColor: '#D1D5DB', strokeDashArray: 3 },
      markers: { size: 0, hover: { size: 8 } },
      yaxis: {
        labels: {
          formatter: function (value) {
            return Math.round(value);
          }
        }
      },
      noData: {
        text: 'No data available',
        align: 'center',
        verticalAlign: 'middle'
      }
    }
  };

  const blockWiseChart = {
    series: [{
      name: 'Registrations',
      data: blockWiseTrends?.blocks?.map(block => 
        block.monthly_data.reduce((sum, month) => sum + month.registrations, 0)
      ) || []
    }],
    options: {
      chart: { type: 'bar', height: 300, toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
      xaxis: { categories: blockWiseTrends?.blocks?.map(block => block.block_name) || [] },
      colors: ['#487FFF'],
      grid: { borderColor: '#D1D5DB', strokeDashArray: 3 },
      yaxis: {
        labels: {
          formatter: function (value) {
            return Math.round(value);
          }
        }
      },
      noData: {
        text: 'No data available',
        align: 'center',
        verticalAlign: 'middle'
      }
    }
  };

  const riskDistributionChart = {
    series: [
      highRiskAnalysis?.total_high_risk || 0, 
      (stats?.total_pregnant_women || 0) - (highRiskAnalysis?.total_high_risk || 0)
    ],
    options: {
      chart: { type: 'donut', height: 300 },
      labels: ['High Risk', 'Normal Risk'],
      colors: ['#FF6B6B', '#4ECDC4'],
      legend: { position: 'bottom' },
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          return Math.round(val) + '%';
        }
      },
      noData: {
        text: 'No data available',
        align: 'center',
        verticalAlign: 'middle'
      }
    }
  };

  const createMiniChart = (data, color) => {
    return {
      series: [{ data: data || [] }],
      options: {
        chart: { type: 'area', height: 60, sparkline: { enabled: true } },
        stroke: { curve: 'smooth', width: 2, colors: [color] },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.4,
            opacityTo: 0.1,
            stops: [0, 100]
          }
        },
        colors: [color],
        noData: {
          text: '',
          align: 'center',
          verticalAlign: 'middle'
        }
      }
    };
  };

  // Block-wise performance chart
  const selectedBlockData = selectedBlock === 'all'
    ? null
    : blockWiseTrends?.blocks?.find(b => b.block_id === parseInt(selectedBlock));

  const blockWisePerformanceChart = blockWiseTrends ? (
    selectedBlockData ? {
      series: [
        { name: 'Registrations', data: selectedBlockData.monthly_data.map(m => m.registrations) },
        { name: 'USG Appointments', data: selectedBlockData.monthly_data.map(m => m.appointments) },
        { name: 'Grievances', data: selectedBlockData.monthly_data.map(m => m.grievances) }
      ],
      options: {
        chart: { type: 'line', height: 300, toolbar: { show: false } },
        stroke: { curve: 'smooth', width: 2 },
        xaxis: { categories: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'] },
        colors: ['#487FFF', '#28a745', '#FFA500'],
        legend: { position: 'top' },
        dataLabels: { enabled: false },
        noData: { text: 'No data available', align: 'center', verticalAlign: 'middle' }
      }
    } : {
      series: [
        {
          name: 'Registrations',
          data: blockWiseTrends.blocks?.map(b =>
            b.monthly_data.reduce((sum, m) => sum + m.registrations, 0)
          ) || []
        },
        {
          name: 'USG Appointments',
          data: blockWiseTrends.blocks?.map(b =>
            b.monthly_data.reduce((sum, m) => sum + m.appointments, 0)
          ) || []
        }
      ],
      options: {
        chart: { type: 'bar', height: 300, toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
        xaxis: { categories: blockWiseTrends.blocks?.map(b => b.block_name) || [] },
        colors: ['#487FFF', '#28a745'],
        legend: { position: 'top' },
        dataLabels: { enabled: false },
        noData: { text: 'No data available', align: 'center', verticalAlign: 'middle' }
      }
    }
  ) : null;

  // Grievance by block chart
  const grievanceByBlockChart = districtGrievanceTrends ? {
    series: districtGrievanceTrends.datasets?.map(ds => ({ name: ds.label, data: ds.data })) || [],
    options: {
      chart: { type: 'bar', height: 300, stacked: true, toolbar: { show: false } },
      plotOptions: { bar: { horizontal: false } },
      xaxis: { categories: districtGrievanceTrends.labels || [] },
      colors: ['#FFA500', '#4169E1', '#228B22', '#8B0000'],
      legend: { position: 'top' },
      dataLabels: { enabled: false },
      noData: { text: 'No data available', align: 'center', verticalAlign: 'middle' }
    }
  } : null;

  // High risk timeline chart
  const highRiskTimelineChart = districtHighRiskTimeline ? {
    series: districtHighRiskTimeline.datasets?.map(ds => ({ name: ds.label, data: ds.data })) || [],
    options: {
      chart: { type: 'area', height: 300, toolbar: { show: false } },
      stroke: { curve: 'smooth', width: 2 },
      xaxis: { categories: districtHighRiskTimeline.labels || [] },
      colors: ['#487FFF', '#DC143C'],
      fill: { type: 'gradient', gradient: { opacityFrom: 0.6, opacityTo: 0.1 } },
      legend: { position: 'top' },
      dataLabels: { enabled: false },
      noData: { text: 'No data available', align: 'center', verticalAlign: 'middle' }
    }
  } : null;

  // DP-wise performance chart
  const dpPerformanceChart = dpPerformance?.dp_performance?.length > 0 ? {
    series: [{
      name: 'Completed Deliveries',
      data: dpPerformance.dp_performance.map(dp => dp.completed)
    }, {
      name: 'Total Referrals',
      data: dpPerformance.dp_performance.map(dp => dp.total_referrals_received)
    }],
    options: {
      chart: { type: 'bar', height: 300, toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
      xaxis: { categories: dpPerformance.dp_performance.map(dp => dp.dp_name) },
      colors: ['#28a745', '#487FFF'],
      legend: { position: 'top' },
      dataLabels: { enabled: false },
      noData: { text: 'No data available', align: 'center', verticalAlign: 'middle' }
    }
  } : null;

  // Delivery trends donut chart
  const deliveryTrendsChart = deliverySummary?.outcome_summary?.by_type &&
    Object.keys(deliverySummary.outcome_summary.by_type).length > 0 ? {
    series: Object.values(deliverySummary.outcome_summary.by_type),
    options: {
      chart: { type: 'donut', height: 300 },
      labels: Object.keys(deliverySummary.outcome_summary.by_type).map(k =>
        k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      ),
      colors: ['#28a745', '#4ECDC4', '#FFA500', '#DC143C', '#8B0000'],
      legend: { position: 'bottom' },
      dataLabels: { enabled: true, formatter: val => Math.round(val) + '%' },
      noData: { text: 'No data available', align: 'center', verticalAlign: 'middle' }
    }
  } : null;

  if (loading) {
    return (
      <MasterLayout>
        <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb title="District Dashboard" />
      
      <div className="row row-cols-xxxl-5 row-cols-lg-3 row-cols-sm-2 row-cols-1 gy-4">
        <div className="col">
          <div className="card shadow-none border bg-gradient-start-1 h-100">
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">Total Pregnant Women</p>
                  <h6 className="mb-0">{stats?.total_pregnant_women || 0}</h6>
                </div>
                <div className="w-50-px h-50-px bg-cyan rounded-circle d-flex justify-content-center align-items-center">
                  <Icon
                    icon="gridicons:multiple-users"
                    className="text-white text-2xl mb-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card shadow-none border bg-gradient-start-2 h-100">
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">High Risk Cases</p>
                  <h6 className="mb-0">{stats?.high_risk_cases || 0}</h6>
                </div>
                <div className="w-50-px h-50-px bg-red rounded-circle d-flex justify-content-center align-items-center">
                  <Icon
                    icon="material-symbols:warning"
                    className="text-white text-2xl mb-0"
                  />
                </div>
              </div>

            </div>
          </div>
        </div>
        <div className="col">
          <div className="card shadow-none border h-100" style={{background: 'linear-gradient(135deg, #fff5f5 0%, #ffe0e0 100%)'}}>
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">Active High Risk</p>
                  <h6 className="mb-0">{stats?.active_high_risk_cases || 0}</h6>
                </div>
                <div className="w-50-px h-50-px bg-danger rounded-circle d-flex justify-content-center align-items-center">
                  <Icon
                    icon="material-symbols:personal-injury"
                    className="text-white text-2xl mb-0"
                  />
                </div>
              </div>

            </div>
          </div>
        </div>
        <div className="col">
          <div className="card shadow-none border bg-gradient-start-3 h-100">
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">USG Appointments</p>
                  <h6 className="mb-0">{stats?.total_usg_appointments || 0}</h6>
                </div>
                <div className="w-50-px h-50-px bg-info rounded-circle d-flex justify-content-center align-items-center">
                  <Icon
                    icon="material-symbols:calendar-month"
                    className="text-white text-2xl mb-0"
                  />
                </div>
              </div>

            </div>
          </div>
        </div>
        <div className="col">
          <div className="card shadow-none border bg-gradient-start-4 h-100">
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">Active Cases</p>
                  <h6 className="mb-0">{stats?.active_pregnant_women || 0}</h6>
                </div>
                <div className="w-50-px h-50-px bg-success-main rounded-circle d-flex justify-content-center align-items-center">
                  <Icon
                    icon="material-symbols:favorite"
                    className="text-white text-2xl mb-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card shadow-none border bg-gradient-start-5 h-100">
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">Completion Rate</p>
                  <h6 className="mb-0">
                    {stats?.total_usg_appointments > 0
                      ? `${Math.round((stats.completed_usg_appointments / stats.total_usg_appointments) * 100)}%`
                      : '0%'}
                  </h6>
                </div>
                <div className="w-50-px h-50-px bg-purple rounded-circle d-flex justify-content-center align-items-center">
                  <Icon
                    icon="material-symbols:check-circle"
                    className="text-white text-2xl mb-0"
                  />
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Delivery Stats Row */}
      <div className="d-flex align-items-center gap-2 mt-24 mb-16">
        <h6 className="text-lg fw-semibold mb-0">Delivery Statistics</h6>
        {/* <span className="px-12 py-4 rounded-pill fw-medium text-sm bg-primary-100 text-primary-600">Live</span> */}
      </div>
      <div className="row row-cols-xxxl-4 row-cols-lg-4 row-cols-sm-2 row-cols-1 gy-4 mt-1">
        <div className="col">
          <div className="card shadow-none border bg-gradient-start-1 h-100">
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">Total Referrals</p>
                  <h6 className="mb-0">{stats?.total_referrals || 0}</h6>
                </div>
                <div className="w-50-px h-50-px bg-cyan rounded-circle d-flex justify-content-center align-items-center">
                  <Icon icon="material-symbols:local-shipping" className="text-white text-2xl mb-0" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card shadow-none border bg-gradient-start-2 h-100">
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">Pending Referrals</p>
                  <h6 className="mb-0">{stats?.pending_referrals || 0}</h6>
                </div>
                <div className="w-50-px h-50-px bg-red rounded-circle d-flex justify-content-center align-items-center">
                  <Icon icon="material-symbols:pending-actions" className="text-white text-2xl mb-0" />
                </div>
              </div>

            </div>
          </div>
        </div>
        <div className="col">
          <div className="card shadow-none border bg-gradient-start-3 h-100">
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">Completed Deliveries</p>
                  <h6 className="mb-0">{stats?.completed_deliveries || 0}</h6>
                </div>
                <div className="w-50-px h-50-px bg-success-main rounded-circle d-flex justify-content-center align-items-center">
                  <Icon icon="material-symbols:done-all" className="text-white text-2xl mb-0" />
                </div>
              </div>

            </div>
          </div>
        </div>
        <div className="col">
          <div className="card shadow-none border bg-gradient-start-4 h-100">
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">Total Outcomes</p>
                  <h6 className="mb-0">{stats?.total_outcomes || 0}</h6>
                </div>
                <div className="w-50-px h-50-px bg-info rounded-circle d-flex justify-content-center align-items-center">
                  <Icon icon="material-symbols:child-care" className="text-white text-2xl mb-0" />
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ECG Stats Row */}
      <div className="d-flex align-items-center gap-2 mt-24 mb-16">
        <h6 className="text-lg fw-semibold mb-0">ECG Statistics</h6>
      </div>
      <div className="row row-cols-xxxl-3 row-cols-lg-3 row-cols-sm-2 row-cols-1 gy-4 mt-1">
        <div className="col">
          <div className="card shadow-none border bg-gradient-start-1 h-100">
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">Total ECG Reports</p>
                  <h6 className="mb-0">{ecgSummary?.summary?.total_ecg_reports || 0}</h6>
                </div>
                <div className="w-50-px h-50-px bg-cyan rounded-circle d-flex justify-content-center align-items-center">
                  <Icon icon="mdi:heart-pulse" className="text-white text-2xl mb-0" />
                </div>
              </div>

            </div>
          </div>
        </div>
        <div className="col">
          <div className="card shadow-none border bg-gradient-start-2 h-100">
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">Normal ECG</p>
                  <h6 className="mb-0">{ecgSummary?.summary?.normal || 0}</h6>
                </div>
                <div className="w-50-px h-50-px bg-success-main rounded-circle d-flex justify-content-center align-items-center">
                  <Icon icon="mdi:heart-check" className="text-white text-2xl mb-0" />
                </div>
              </div>

            </div>
          </div>
        </div>
        <div className="col">
          <div className="card shadow-none border bg-gradient-start-3 h-100">
            <div className="card-body p-20">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <p className="fw-medium text-primary-light mb-1">Abnormal ECG</p>
                  <h6 className="mb-0">{ecgSummary?.summary?.abnormal || 0}</h6>
                </div>
                <div className="w-50-px h-50-px bg-red rounded-circle d-flex justify-content-center align-items-center">
                  <Icon icon="mdi:heart-broken" className="text-white text-2xl mb-0" />
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
      
      <section className="row gy-4 mt-1">
        
        <div className="col-xxl-8">
          <div className="card h-100">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center justify-content-between">
                <h6 className="text-lg mb-0">Registration Trends</h6>
                <select className="form-select bg-base form-select-sm w-auto" value={ancViewFilter} onChange={(e) => setAncViewFilter(e.target.value)}>
                  <option value="Yearly">Yearly</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Today">Today</option>
                </select>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2 mt-8">
                <h6 className="mb-0">{getTotalVisits()}</h6>
                {/* {getTrendPercentage() !== null && (
                  <span className={`text-sm fw-semibold rounded-pill px-8 py-4 line-height-1 d-flex align-items-center gap-1 border ${
                    getTrendPercentage() >= 0
                      ? 'bg-success-focus text-success-main br-success'
                      : 'bg-danger-focus text-danger-main br-danger'
                  }`}>
                    {getTrendPercentage() >= 0 ? '+' : ''}{getTrendPercentage()}%
                    <Icon icon={getTrendPercentage() >= 0 ? 'bxs:up-arrow' : 'bxs:down-arrow'} className="text-xs" />
                  </span>
                )} */}
              </div>
              <ReactApexChart {...monthlyTrendsChart} type="area" height={264} />
            </div>
          </div>
        </div>
        
        <div className="col-xxl-4">
          <div className="card h-100">
            <div className="card-header border-bottom bg-base py-16 px-24">
              <h6 className="text-lg fw-semibold mb-0">Risk Category Distribution</h6>
            </div>
            <div className="card-body p-24">
              <ReactApexChart {...riskDistributionChart} type="donut" height={300} />
            </div>
          </div>
        </div>
      </section>

      {/* Block-wise Performance */}
      <section className="row gy-4 mt-1">
        <div className="col-12">
          <div className="card">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
              <h6 className="text-lg fw-semibold mb-0">
                {selectedBlock === 'all'
                  ? 'Block-wise Performance'
                  : `${blockWiseTrends?.blocks?.find(b => b.block_id === parseInt(selectedBlock))?.block_name || ''} — Monthly Trend`
                }
              </h6>
              <select
                className="form-select bg-base form-select-sm w-auto"
                value={selectedBlock}
                onChange={(e) => setSelectedBlock(e.target.value)}
              >
                <option value="all">All Blocks</option>
                {blockWiseTrends?.blocks?.map(b => (
                  <option key={b.block_id} value={b.block_id}>{b.block_name}</option>
                ))}
              </select>
            </div>
            <div className="card-body p-24">
              {blockWisePerformanceChart ? (
                <ReactApexChart
                  {...blockWisePerformanceChart}
                  type={selectedBlock === 'all' ? 'bar' : 'line'}
                  height={300}
                />
              ) : (
                <div className="text-center py-5 text-secondary-light">No data available</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Grievance Trends by Block + High Risk Timeline */}
      <section className="row gy-4 mt-1">
        <div className="col-xxl-6">
          <div className="card h-100">
            <div className="card-header border-bottom bg-base py-16 px-24">
              <h6 className="text-lg fw-semibold mb-0">Grievance Trends by Block</h6>
            </div>
            <div className="card-body p-24">
              {grievanceByBlockChart ? (
                <ReactApexChart {...grievanceByBlockChart} type="bar" height={300} />
              ) : (
                <div className="text-center py-5 text-secondary-light">No data available</div>
              )}
            </div>
          </div>
        </div>
        <div className="col-xxl-6">
          <div className="card h-100">
            <div className="card-header border-bottom bg-base py-16 px-24">
              <h6 className="text-lg fw-semibold mb-0">High Risk Timeline (Last 6 Months)</h6>
            </div>
            <div className="card-body p-24">
              {highRiskTimelineChart ? (
                <ReactApexChart {...highRiskTimelineChart} type="area" height={300} />
              ) : (
                <div className="text-center py-5 text-secondary-light">No data available</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* DP-wise Trends + Delivery Trends */}
      <section className="row gy-4 mt-1">
        <div className="col-xxl-8">
          <div className="card h-100">
            <div className="card-header border-bottom bg-base py-16 px-24">
              <h6 className="text-lg fw-semibold mb-0">Delivery Point Performance</h6>
            </div>
            <div className="card-body p-24">
              {dpPerformanceChart ? (
                <ReactApexChart {...dpPerformanceChart} type="bar" height={300} />
              ) : (
                <div className="text-center py-5 text-secondary-light">No delivery data available</div>
              )}
            </div>
          </div>
        </div>
        <div className="col-xxl-4">
          <div className="card h-100">
            <div className="card-header border-bottom bg-base py-16 px-24">
              <h6 className="text-lg fw-semibold mb-0">Delivery Outcome Trends</h6>
            </div>
            <div className="card-body p-24">
              {deliveryTrendsChart ? (
                <ReactApexChart {...deliveryTrendsChart} type="donut" height={300} />
              ) : (
                <div className="text-center py-5 text-secondary-light">No delivery outcome data available</div>
              )}
            </div>
          </div>
        </div>
      </section>
    </MasterLayout>
  );
};

export default DashboardDistrictPage;
